import React, { useEffect, useState, useRef } from 'react';
import { CheckoutData } from '../../pages/checkout/HostingCheckout';
import { HostingPackage } from '../../services/hostingService';

interface ConfirmationStepProps {
  data: CheckoutData;
  package: HostingPackage;
  serviceType?: string;
}

// Phase 1 #1: hosting provisioning live status. Frontend polls
// /api/hosting/provisioning/order/:orderId until status reaches a terminal
// state. Same pattern as DomainConfirmationStep.
type HostingProvStatus =
  | 'not_started' | 'pending' | 'in_progress'
  | 'completed' | 'failed' | 'awaiting_admin';

interface HostingProvData {
  status: HostingProvStatus;
  lastStep?: string;
  lastError?: string;
  hostingAccountId?: string;
  containerVmid?: number;
  primaryDomain?: string;
}

const POLL_INTERVAL_MS = 4000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Generic post-payment confirmation for hosting / VPS / email orders.
 *
 * Previous version made claims that weren't always true:
 *   - "Account Created Successfully" with the chosen password — only valid
 *     when there used to be a PasswordSetupStep; with login enforced upstream
 *     (F4) that block is gone.
 *   - "Welcome back" greeting that referenced customer-type heuristics that
 *     are unreliable.
 *   - Fixed timings ("5-10 minutes", "15-30 minutes") regardless of service.
 *
 * This version is honest: shows the real order ID and what we charged, names
 * the service explicitly, and explains the next-steps based on what the
 * webhook + provisioning pipeline actually does.
 */
const ConfirmationStep: React.FC<ConfirmationStepProps> = ({ data, package: pkg, serviceType }) => {
  const orderNumber = data.orderNumber || data.orderId || 'ORDER-PENDING';
  const serviceLabel = serviceType || 'Hosting';
  const showDomain = !!data.domainName;
  const isDomainRegistration = data.domainAction === 'register';
  const isHostingOrder = !serviceType || serviceType === 'Hosting';
  const isEmailOrder = serviceType === 'Email';
  const pricing: any = data.pricing || {};
  const num = (v: unknown): number => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
    return Number.isFinite(n) ? n : 0;
  };
  const totalPaid = num(pricing.total);
  const yearlySavings = num(pricing.yearlySavings);

  // Poll hosting provisioning when this confirmation page is for a
  // hosting order. Skips polling for VPS / Email confirmations.
  const [hostingProv, setHostingProv] = useState<HostingProvData>({ status: 'not_started' });
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStartRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!isHostingOrder || !data.orderId) return;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      if (Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) return;
      try {
        const apiBase = process.env.REACT_APP_API_URL || '';
        const token = localStorage.getItem('token');
        const resp = await fetch(`${apiBase}/api/hosting/provisioning/order/${data.orderId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const json = await resp.json();
        if (!cancelled && json?.success && json.data) {
          setHostingProv(json.data);
          if (['completed', 'failed', 'awaiting_admin'].includes(json.data.status)) return;
        }
      } catch (e: any) {
        console.warn('Hosting provisioning poll error:', e?.message);
      }
      pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => {
      cancelled = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [data.orderId, isHostingOrder]);

  const provStepLabel = (s?: string) => {
    if (!s) return 'Starting…';
    return ({
      pick_host: 'Picking a server',
      reserve_vmid: 'Reserving container ID',
      clone_template: 'Cloning hosting template',
      apply_quotas: 'Applying plan limits',
      inject_env_and_start: 'Configuring your account',
      capture_ip: 'Assigning IP address',
      persist: 'Finalizing account',
    } as Record<string, string>)[s] || s;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-2xl mx-auto text-center">
        {/* Success Icon */}
        <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
          <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        {/* Success Message */}
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Payment Received</h2>
        <p className="text-lg text-gray-600 mb-8">
          Thank you. Your {serviceLabel.toLowerCase()} order has been recorded and our team is provisioning your service.
        </p>

        {/* Order Details */}
        <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Details</h3>

          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Order ID:</span>
              <span className="font-mono font-medium">{orderNumber}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">{serviceLabel} Plan:</span>
              <span className="font-medium">{pkg.name}</span>
            </div>

            {showDomain && (
              <div className="flex justify-between">
                <span className="text-gray-600">Domain:</span>
                <span className="font-medium">{data.domainName}</span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-gray-600">Billing Cycle:</span>
              <span className="font-medium">
                {data.billingCycle === 'yearly' ? 'Annual' : 'Monthly'}
                {data.billingCycle === 'yearly' && yearlySavings > 0 && (
                  <span className="text-green-700 text-sm ml-1">(saved ${yearlySavings.toFixed(2)} vs monthly)</span>
                )}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">Customer Email:</span>
              <span className="font-medium">{data.contactInfo.email}</span>
            </div>

            <div className="border-t pt-3">
              <div className="flex justify-between text-lg font-bold">
                <span>Total Paid:</span>
                <span className="text-brand-orange">${totalPaid.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Live hosting provisioning status (hosting orders only) */}
        {isHostingOrder && data.orderId && (
          <div className="rounded-lg p-6 mb-8 text-left bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">Provisioning Your Hosting Account</h3>
            {hostingProv.status === 'completed' ? (
              <div className="text-sm text-blue-900">
                ✅ Your hosting account is live. Container <span className="font-mono">#{hostingProv.containerVmid}</span> on our shared infrastructure.
                {hostingProv.hostingAccountId && (
                  <div className="mt-2">
                    <a className="underline font-medium" href={`/customer/hosting/${hostingProv.hostingAccountId}`}>
                      Open your hosting dashboard →
                    </a>
                  </div>
                )}
              </div>
            ) : hostingProv.status === 'failed' || hostingProv.status === 'awaiting_admin' ? (
              <div className="text-sm">
                <div className="font-medium text-amber-900">Setup needs attention</div>
                <div className="text-amber-800 mt-1">
                  We've received your payment, but provisioning ran into an issue. Our team has been notified and will follow up. You will not be charged again.
                </div>
                {hostingProv.lastError && (
                  <div className="text-xs text-amber-700 mt-2 font-mono break-words">{hostingProv.lastError}</div>
                )}
              </div>
            ) : (
              <div className="text-sm text-blue-900 flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-3"></div>
                <span>{provStepLabel(hostingProv.lastStep)}… (this usually takes 30–60 seconds)</span>
              </div>
            )}
          </div>
        )}

        {/* Next Steps */}
        <div className="bg-blue-50 rounded-lg p-6 mb-8 text-left">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">What Happens Next?</h3>

          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center mr-3">
                <span className="text-blue-800 font-semibold text-sm">1</span>
              </div>
              <div>
                <div className="font-medium text-blue-900">{serviceLabel} provisioning</div>
                <div className="text-sm text-blue-700">
                  {isEmailOrder
                    ? 'We\'re creating your mail domain now. Next, the setup wizard walks you through the DNS records and your first mailbox.'
                    : <>We're setting up your {serviceLabel.toLowerCase()} service. You'll see it in your dashboard as soon as it's ready.</>}
                </div>
              </div>
            </div>

            {showDomain && isDomainRegistration && (
              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center mr-3">
                  <span className="text-blue-800 font-semibold text-sm">2</span>
                </div>
                <div>
                  <div className="font-medium text-blue-900">Domain registration</div>
                  <div className="text-sm text-blue-700">
                    Your domain {data.domainName} is being registered with AWS Route 53. Typically 1-10 minutes. DNS propagation can take up to 48 hours after that.
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center mr-3">
                <span className="text-blue-800 font-semibold text-sm">{showDomain && isDomainRegistration ? '3' : '2'}</span>
              </div>
              <div>
                <div className="font-medium text-blue-900">Confirmation email</div>
                <div className="text-sm text-blue-700">You'll receive a confirmation email at {data.contactInfo.email} with setup instructions.</div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-orange-50 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-orange-900 mb-2">Need Help?</h3>
          <p className="text-orange-800 mb-4">
            Our support team is here to help you get started.
          </p>
          <div className="text-sm text-orange-700 space-y-1">
            <div>📧 Email: support@12and22.com</div>
            <div>🎫 Or open a ticket from <a href="/dashboard/support" className="underline">Support</a> in your dashboard</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => window.location.href = isEmailOrder ? '/customer/email' : '/dashboard'}
            className="px-8 py-3 bg-brand-orange text-white rounded-md hover:bg-secondary-600 transition-colors font-semibold"
          >
            {isEmailOrder ? 'Set up your email →' : 'Go to Dashboard'}
          </button>
          <button
            onClick={() => window.location.href = isEmailOrder ? '/dashboard' : '/hosting'}
            className="px-8 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            {isEmailOrder ? 'Go to Dashboard' : 'Browse More Plans'}
          </button>
        </div>

        {/* Order Reference */}
        <div className="mt-8 pt-6 border-t text-center">
          <p className="text-sm text-gray-500">
            Please save this confirmation for your records. Order ID: <span className="font-mono">{orderNumber}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationStep;
